import {
  type ComponentProps,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTheme } from '@mui/material';
import { MicrophoneContext } from '../MicrophoneProvider';
import { StyledCanvas } from './styled';

type VisualiserProps = ComponentProps<typeof StyledCanvas>;

const Visualiser = (props: VisualiserProps) => {
  const theme = useTheme();

  const { audioSource, audioContext } = useContext(MicrophoneContext);
  const animationFrameIdRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const analyserNode = useMemo(() => {
    if (!audioContext) {
      return null;
    }
    const analyser = audioContext?.createAnalyser();
    analyser.fftSize = 1024;
    return analyser;
  }, [audioContext]);

  const drawWaveform = useCallback(() => {
    if (!analyserNode || !canvasRef.current) {
      return;
    }
    const canvas = canvasRef.current;
    const canvasContext = canvas.getContext('2d');
    if (!canvasContext) {
      return;
    }

    const bufferLength = analyserNode.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      analyserNode.getByteTimeDomainData(dataArray);
      canvasContext.clearRect(0, 0, canvas.width, canvas.height);
      canvasContext.lineWidth = 4;
      canvasContext.strokeStyle = theme.palette.grey['600'];
      canvasContext.beginPath();

      const sliceWidth = canvas.width / bufferLength;
      let x = 0;

      for (let i = 0; i < bufferLength; i += 1) {
        const value = dataArray[i] / 128.0;
        const y = (value * canvas.height) / 2;

        if (i === 0) {
          canvasContext.moveTo(x, y);
        } else {
          canvasContext.lineTo(x, y);
        }

        x += sliceWidth;
      }

      canvasContext.lineTo(canvas.width, canvas.height / 2);
      canvasContext.stroke();

      animationFrameIdRef.current = requestAnimationFrame(draw);
    };

    draw();
  }, [analyserNode, canvasRef, theme]);

  useEffect(() => {
    if (!analyserNode || !audioSource) {
      return;
    }
    audioSource.connect(analyserNode);
    drawWaveform();

    return () => {
      audioSource.disconnect(analyserNode);
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
    };
  }, [analyserNode, audioSource, drawWaveform]);

  return <StyledCanvas ref={canvasRef} {...props} />;
};

export default Visualiser;
